import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { miniGamesAPI } from '@/lib/api';
import { ArrowLeft, Play, Pause, RotateCcw, Trophy, Medal } from 'lucide-react';

const GRID_SIZE = 20;
const CELL_SIZE = 20;
const INITIAL_SNAKE = [{ x: 10, y: 10 }];
const INITIAL_DIRECTION = { x: 1, y: 0 };
const GAME_SPEED = 100;

export default function SnakeGamePage() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [direction, setDirection] = useState(INITIAL_DIRECTION);
  const [food, setFood] = useState({ x: 15, y: 15 });
  const [gameOver, setGameOver] = useState(false);
  const [gamePaused, setGamePaused] = useState(true);
  const [score, setScore] = useState(0);
  const [personalBest, setPersonalBest] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const gameLoopRef = useRef(null);
  const directionQueueRef = useRef([]);

  // Fetch leaderboard and personal best
  useEffect(() => {
    fetchLeaderboard();
    fetchPersonalBest();

    // Auto-refresh leaderboard every 30 seconds
    const interval = setInterval(() => {
      fetchLeaderboard();
      fetchPersonalBest();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await miniGamesAPI.getLeaderboard('snake', 10);
      setLeaderboard(response.data.scores || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const fetchPersonalBest = async () => {
    try {
      const response = await miniGamesAPI.getPersonalBest('snake');
      setPersonalBest(response.data.best_score || 0);
    } catch (error) {
      console.error('Error fetching personal best:', error);
    }
  };

  // Generate random food position
  const generateFood = (currentSnake) => {
    let newFood;
    do {
      newFood = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE),
      };
    } while (currentSnake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    return newFood;
  };

  // Game loop
  useEffect(() => {
    if (gamePaused || gameOver) {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
      return;
    }

    gameLoopRef.current = setInterval(() => {
      // Process direction queue BEFORE setSnake
      const nextDirection = directionQueueRef.current.shift();
      if (nextDirection) {
        setDirection(nextDirection);
      }

      setSnake(prevSnake => {
        const head = prevSnake[0];
        const newHead = {
          x: head.x + (nextDirection?.x ?? direction.x),
          y: head.y + (nextDirection?.y ?? direction.y),
        };

        // Check wall collision
        if (newHead.x < 0 || newHead.x >= GRID_SIZE || newHead.y < 0 || newHead.y >= GRID_SIZE) {
          setGameOver(true);
          return prevSnake;
        }

        // Check self collision
        if (prevSnake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
          setGameOver(true);
          return prevSnake;
        }

        const newSnake = [newHead, ...prevSnake];

        // Check food collision
        if (newHead.x === food.x && newHead.y === food.y) {
          setScore(prev => prev + 10);
          setFood(generateFood(newSnake));
        } else {
          newSnake.pop();
        }

        return newSnake;
      });
    }, GAME_SPEED);

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [gamePaused, gameOver, direction, food]);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameOver) return;

      let newDirection = null;
      const currentDirection = directionQueueRef.current.length > 0
        ? directionQueueRef.current[directionQueueRef.current.length - 1]
        : direction;

      switch (e.key) {
        case 'ArrowUp':
          if (currentDirection.y === 0) newDirection = { x: 0, y: -1 };
          break;
        case 'ArrowDown':
          if (currentDirection.y === 0) newDirection = { x: 0, y: 1 };
          break;
        case 'ArrowLeft':
          if (currentDirection.x === 0) newDirection = { x: -1, y: 0 };
          break;
        case 'ArrowRight':
          if (currentDirection.x === 0) newDirection = { x: 1, y: 0 };
          break;
        case ' ':
          e.preventDefault();
          setGamePaused(prev => !prev);
          break;
        default:
          return;
      }

      if (newDirection) {
        e.preventDefault();
        directionQueueRef.current.push(newDirection);
        if (directionQueueRef.current.length > 2) {
          directionQueueRef.current.shift();
        }
        if (gamePaused) {
          setGamePaused(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [direction, gamePaused, gameOver]);

  // Draw game
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    ctx.strokeStyle = '#2a2a2a';
    for (let i = 0; i <= GRID_SIZE; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL_SIZE, 0);
      ctx.lineTo(i * CELL_SIZE, GRID_SIZE * CELL_SIZE);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * CELL_SIZE);
      ctx.lineTo(GRID_SIZE * CELL_SIZE, i * CELL_SIZE);
      ctx.stroke();
    }

    // Draw food
    ctx.fillStyle = '#ef4444';
    ctx.fillRect(food.x * CELL_SIZE, food.y * CELL_SIZE, CELL_SIZE, CELL_SIZE);

    // Draw snake
    snake.forEach((segment, index) => {
      ctx.fillStyle = index === 0 ? '#22c55e' : '#16a34a';
      ctx.fillRect(segment.x * CELL_SIZE, segment.y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
    });

    // Draw game over overlay
    if (gameOver) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Game Over!', canvas.width / 2, canvas.height / 2 - 20);
      ctx.font = '24px Arial';
      ctx.fillText(`Score: ${score}`, canvas.width / 2, canvas.height / 2 + 20);
    }
  }, [snake, food, gameOver, score]);

  const handleRestart = () => {
    setSnake(INITIAL_SNAKE);
    setDirection(INITIAL_DIRECTION);
    setFood({ x: 15, y: 15 });
    setGameOver(false);
    setGamePaused(true);
    setScore(0);
    directionQueueRef.current = [];
  };

  const handleSubmitScore = async () => {
    try {
      setSubmitting(true);
      await miniGamesAPI.submitScore('snake', score);
      await fetchLeaderboard();
      await fetchPersonalBest();
      handleRestart();
    } catch (error) {
      console.error('Error submitting score:', error);
      alert('Fout bij opslaan score: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
    });
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" onClick={() => navigate('/mini-games')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug
        </Button>
        <div>
          <h1 className="text-3xl font-bold">🐍 Snake</h1>
          <p className="text-gray-400">Eet de appels en groei!</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Game Canvas */}
        <div className="lg:col-span-2">
          <Card className="border-green-500/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  Score: <span className="text-green-400">{score}</span>
                </CardTitle>
                <div className="flex gap-2">
                  {!gameOver && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setGamePaused(!gamePaused)}
                    >
                      {gamePaused ? (
                        <><Play className="w-4 h-4 mr-2" /> Start</>
                      ) : (
                        <><Pause className="w-4 h-4 mr-2" /> Pauze</>
                      )}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={handleRestart}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Opnieuw
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <canvas
                  ref={canvasRef}
                  width={GRID_SIZE * CELL_SIZE}
                  height={GRID_SIZE * CELL_SIZE}
                  className="border-2 border-green-500/50 rounded"
                />
              </div>

              {/* Controls */}
              <div className="mt-4 p-4 bg-gray-800/50 rounded-lg">
                <h3 className="font-semibold mb-2">Besturing:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-400">
                  <div>• Pijltjestoetsen: bewegen</div>
                  <div>• Spatiebalk: pauze</div>
                </div>
              </div>

              {/* Submit Score */}
              {gameOver && (
                <div className="mt-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
                  <p className="text-center mb-3">
                    {score > personalBest ? (
                      <span className="text-green-400 font-bold">Nieuwe persoonlijke record!</span>
                    ) : (
                      <span>Je score: {score}</span>
                    )}
                  </p>
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handleSubmitScore}
                    disabled={submitting}
                  >
                    {submitting ? 'Opslaan...' : 'Score Opslaan'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Personal Best */}
          <Card className="border-green-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Medal className="w-5 h-5 text-green-400" />
                Persoonlijk Record
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-green-400">{personalBest}</p>
            </CardContent>
          </Card>

          {/* Leaderboard */}
          <Card className="border-green-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {leaderboard.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">Nog geen scores</p>
                ) : (
                  leaderboard.map((entry, index) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-2 bg-gray-800/50 rounded"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={index < 3 ? 'text-yellow-400' : ''}>
                          #{index + 1}
                        </Badge>
                        <div>
                          <p className="font-semibold">{entry.username}</p>
                          <p className="text-xs text-gray-400">
                            {formatDate(entry.created_at)}
                          </p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-green-400">{entry.score}</span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
